import { Component, ErrorInfo, ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ConvexProvider, ConvexReactClient } from "convex/react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { logClientError } from "@/lib/convex";
import { detectBrowserLanguage } from "@/lib/ui-translations";
import { useNewVersionWatch } from "@/lib/versionWatch";
import posthog from "posthog-js";

const convex = new ConvexReactClient(import.meta.env.VITE_CONVEX_URL);

// Localized strings for the App-level error boundary. React Error Boundaries
// cannot use hooks, so we resolve translations from navigator.language directly.
const ERROR_BOUNDARY_STRINGS: Record<string, { title: string; body: string; cta: string }> = {
  fr: {
    title: 'Une erreur est survenue',
    body: 'Veuillez rafraîchir la page pour continuer. Si le problème persiste, contactez votre cabinet.',
    cta: 'Rafraîchir la page',
  },
  en: {
    title: 'Something went wrong',
    body: 'Please refresh the page to continue. If the problem persists, contact your firm.',
    cta: 'Refresh the page',
  },
  es: {
    title: 'Ocurrió un error',
    body: 'Actualice la página para continuar. Si el problema persiste, comuníquese con su consultor.',
    cta: 'Actualizar la página',
  },
};

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logClientError('ErrorBoundary', error, { componentStack: info.componentStack ?? undefined });
    posthog.captureException(error);
  }

  render() {
    if (this.state.hasError) {
      const lang = detectBrowserLanguage();
      const strings = ERROR_BOUNDARY_STRINGS[lang] ?? ERROR_BOUNDARY_STRINGS.fr;
      return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
          <div className="bg-card rounded-2xl shadow-card p-6 max-w-md text-center">
            <div className="w-14 h-14 bg-destructive/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-destructive" aria-hidden="true" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">{strings.title}</h2>
            <p className="text-muted-foreground mb-5">
              {strings.body}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-primary text-primary-foreground min-h-[44px] px-5 py-2 rounded-lg text-base font-medium hover:bg-primary/90 transition-colors"
            >
              {strings.cta}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const ExternalRedirect = ({ to }: { to: string }) => {
  window.location.href = to;
  return null;
};

// Offers a reload when the server ships a newer bundle than the one running
// in this (possibly weeks-old) tab. Mounted beside the toasters it relies on.
const NewVersionWatch = () => {
  useNewVersionWatch();
  return null;
};

const App = () => (
  <ErrorBoundary>
  <ConvexProvider client={convex}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <NewVersionWatch />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<ExternalRedirect to="https://formio.ca" />} />
          <Route path="/f/:publicFormId" element={<Index />} />
          <Route path="/app/:clientAppId" element={<Index />} />
          <Route path="/supplement/:supplementId" element={<Index />} />
          <Route path="/:clientId" element={<Index />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </ConvexProvider>
  </ErrorBoundary>
);

export default App;
